import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import { Link, Navigate } from "react-router-dom";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import {
  selectActiveProgramId,
  useMyPrograms,
} from "../../_lib/use-active-program";
import { linkToEpocusAddUser, linkToEpocusSettings } from "../../links";
import { BackToSettingsLink } from "../_lib/back-to-settings-link";
import { SettingsCard } from "../_lib/settings-card";
import { useCanManageProgramUsers } from "../_lib/use-can-manage-program-users";
import { useEpocusProgramUsersQuery } from "./_lib/__generated__/program-users-operations.gql";
import { ProgramUsersList } from "./_lib/program-users-list";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 800,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const HeaderRow = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 16,
});

const LoadingRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: 24,
});

export const EpocusManageUsersPage: FC = () => {
  const canManage = useCanManageProgramUsers();
  const { programs, loading: programsLoading } = useMyPrograms();
  const programId = selectActiveProgramId(programs);
  const { data, loading, error } = useEpocusProgramUsersQuery({
    variables: { programId: programId ?? "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  if (!canManage) {
    return <Navigate to={linkToEpocusSettings()} replace />;
  }

  const members = data?.listProgramUsers;

  return (
    <PageRoot>
      <BackToSettingsLink />
      <HeaderRow>
        <Box>
          <Typography
            variant="h5"
            style={{
              fontWeight: 700,
              color: EPOCUS_TEXT_PRIMARY,
              fontSize: rem(24),
              lineHeight: 1.2,
            }}
          >
            Manage users
          </Typography>
          <Typography
            style={{
              fontSize: rem(13),
              color: EPOCUS_TEXT_SECONDARY,
              marginTop: 4,
            }}
          >
            People in your program
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to={linkToEpocusAddUser()}
        >
          Add user
        </Button>
      </HeaderRow>

      {programsLoading ? (
        <LoadingRow>
          <CircularProgress aria-label="Loading your program" />
        </LoadingRow>
      ) : !programId ? (
        <SettingsCard>
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            Couldn't determine your program.
          </Typography>
        </SettingsCard>
      ) : loading && !data ? (
        <LoadingRow>
          <CircularProgress aria-label="Loading users" />
        </LoadingRow>
      ) : error ? (
        <SettingsCard>
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            Couldn't load users.
          </Typography>
        </SettingsCard>
      ) : !members || members.length === 0 ? (
        <SettingsCard>
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            No users found in your program.
          </Typography>
        </SettingsCard>
      ) : (
        <ProgramUsersList users={members} />
      )}
    </PageRoot>
  );
};
